import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Settings as SettingsIcon } from '@material-ui/icons';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import IconButton from 'Components/Atoms/IconButton';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Container from 'Components/Atoms/Container';
import Tabs from 'Components/Atoms/Tabs';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import FeatureNavigation from 'Components/Molecules/FeatureNavigation';
import GTable, { ContextAction, ListConfigHeader } from 'Components/Organisms/GTable';
import NavBar from 'Components/Organisms/NavBar';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { paths } from 'App/ReservationBook/shared';
import { dateHelper, toEuropeanDate } from 'utils/helper';
import './styles.scss';
import ReleaseAlertsSection from './ReleaseAlertsSection';
import { apiMessage, effectiveRuleFor, VisitWish, visitWishApi, VisitWishSettings } from './visitWishApi';
import { europeanDate, orderLabel } from './settingsFormat';
import { ExclusiveCell, liveOffer } from './ExclusiveOffers';
import { filterOf, matchesSearch, PriorityReach, priorityReach, searchedDate, WishFilter } from './wishFilters';
import WishDetails from './WishDetails';
import WishStatus, { period, timeWindow } from './WishStatus';
import useWishActions from './useWishActions';

type Props = { restaurantId: string };

const FILTERS: { id: WishFilter; label: string; empty: string }[] = [
  {
    id: 'open',
    label: 'Open',
    empty: 'No open visit wishes. Guests can leave one on your booking page when their date is fully booked.',
  },
  {
    id: 'offer',
    label: 'Offer running',
    empty: 'No offer is running. Offers only run for wishes the team marked as exclusive.',
  },
  { id: 'done', label: 'Completed', empty: 'No completed visit wishes yet.' },
];

/** A date from the calendar's "show matching wishes" link (?date=YYYY-MM-DD) prefills the search. */
const searchFromLink = (search: string) => {
  const date = new URLSearchParams(search).get('date');
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? toEuropeanDate(date) : '';
};

const settingsLink = (rId: string) => `/${rId}/apps?appId=visit_wishes`;

/**
 * The work list on a phone: one card per wish with the same content and
 * actions as a table row. The card's main area opens the wish in the mobile modal.
 */
const WishCards: React.FC<{
  wishes: VisitWish[];
  emptySentence: string;
  selectedId: string | null;
  onOpen: (id: string) => void;
  actions: ContextAction<VisitWish>[];
  busyId: string | null;
  priorityOf: (wish: VisitWish) => PriorityReach;
}> = ({ wishes, emptySentence, selectedId, onOpen, actions, busyId, priorityOf }) => {
  const { t } = useTranslation('reservations');
  if (!wishes.length) {
    return (
      <Typography variant="text-3" color="subdued" block className="visit-wish-cards-empty">
        {emptySentence}
      </Typography>
    );
  }
  return (
    <ul className="visit-wish-cards">
      {wishes.map((wish) => {
        const open = wish.id === selectedId;
        const shownActions = actions.filter((action) => action.showCondition?.(wish) ?? true);
        return (
          <li key={wish.id} className={`visit-wish-card ${open ? 'selected' : ''}`} data-testid={`card-${wish.id}`}>
            <button type="button" className="visit-wish-card-main" aria-haspopup="dialog" onClick={() => onOpen(wish.id)}>
              <span className="visit-wish-card-line">
                <span className="visit-wish-card-date">{period(wish)}</span>
                <span className="visit-wish-card-guests">{`${t('Guests')}: ${wish.guests}`}</span>
              </span>
              <span className="visit-wish-card-meta">{timeWindow(wish, t)}</span>
              {wish.minLeadHours ? (
                <span className="visit-wish-card-meta">{t('at least {{hours}} h notice', { hours: wish.minLeadHours })}</span>
              ) : null}
              <span className="visit-wish-card-line">
                <span className="visit-wish-card-name">
                  {wish.contact.name}
                  {wish.vipTags?.length ? ' · VIP' : ''}
                </span>
                <WishStatus wish={wish} />
              </span>
              {priorityOf(wish) !== 'none' ? (
                <span className="visit-wish-card-meta">
                  {`${t('Priority')}: ${wish.priority}`}
                  {priorityOf(wish) === 'some' ? ` · ${t('Only on some days')}` : ''}
                </span>
              ) : null}
            </button>
            {wish.exclusive || liveOffer(wish) ? (
              <div className="visit-wish-card-offer">
                <ExclusiveCell wish={wish} />
              </div>
            ) : null}
            {shownActions.length ? (
              <div className="visit-wish-card-actions" role="group" aria-label={t('Actions')}>
                {shownActions.map((action) => {
                  const label = t(action.tooltipContent ?? '');
                  return (
                    <IconButton
                      key={action.tooltipContent}
                      size="medium"
                      aria-label={label}
                      title={label}
                      disabled={busyId === wish.id}
                      onClick={() => action.onClick(wish)}
                    >
                      {action.icon}
                    </IconButton>
                  );
                })}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

/** The team's work list: open wishes first, offers in progress, the history on request. */
const VisitWishList: React.FC<Props> = ({ restaurantId }) => {
  const { t } = useTranslation('reservations');
  const viewScope = useMemo(() => ({ restaurantId }), [restaurantId]);
  // Below 600px a table has no room for its columns; the list switches to cards.
  const small = useMediaQuery('(max-width:600px)');
  const toast = useToast();
  const location = useLocation();
  const [tab, setTab] = useState<'wishes' | 'releaseAlerts'>('wishes');
  const [filter, setFilter] = useState<WishFilter>('open');
  const [search, setSearch] = useState(() => searchFromLink(location?.search ?? ''));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const linkedWish = new URLSearchParams(location?.search ?? '').get('wishId');
  const linkScope = useMemo(() => ({ restaurantId, linkedWish }), [restaurantId, linkedWish]);
  const appliedLink = useRef<typeof linkScope | null>(null);
  const [loadedFor, setLoadedFor] = useState<typeof viewScope | null>(null);
  const [linkFailed, setLinkFailed] = useState(false);
  const [linkRetry, setLinkRetry] = useState(0);
  const [wishes, setWishes] = useState<VisitWish[]>([]);
  // Where the next page of open / ended wishes starts; null: all of them are loaded.
  const [openCursor, setOpenCursor] = useState<string | null>(null);
  const [endedCursor, setEndedCursor] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [settings, setSettings] = useState<VisitWishSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);

  const scopeRef = useRef<typeof viewScope | null>(viewScope);
  scopeRef.current = viewScope;
  const loadSequence = useRef(0);
  useEffect(() => {
    scopeRef.current = viewScope;
    return () => { if (scopeRef.current === viewScope) scopeRef.current = null; };
  }, [viewScope]);
  const stillShown = useCallback((requestedFor: string) => scopeRef.current === viewScope && requestedFor === restaurantId, [viewScope, restaurantId]);

  const load = useCallback(async () => {
    const requestedFor = restaurantId;
    const sequence = ++loadSequence.current;
    const isCurrentLoad = () => stillShown(requestedFor) && sequence === loadSequence.current;
    setLoading(true);
    setLoadingOlder(false);
    try {
      const [page, current] = await Promise.all([
        visitWishApi.listPage(requestedFor),
        visitWishApi.settings(requestedFor),
      ]);
      if (!isCurrentLoad()) return;
      setWishes(page.wishes);
      setLoadedFor(viewScope);
      appliedLink.current = null;
      setOpenCursor(page.openCursor ?? null);
      setEndedCursor(page.endedCursor ?? null);
      setSettings(current);
      setLoadFailed(false);
    } catch (error) {
      if (!isCurrentLoad()) return;
      setLoadFailed(true);
      toast(apiMessage(error, 'Loading failed'), Severity.ERROR, 'reservations');
    } finally {
      if (isCurrentLoad()) setLoading(false);
    }
    // toast only reports; a new toast function must not load again.
  }, [restaurantId, viewScope, stillShown]);

  useEffect(() => {
    load();
  }, [load]);

  // The ended filter continues the ended wishes, the others the open ones.
  const moreCursor = filter === 'done' ? endedCursor : openCursor;
  const loadMore = async () => {
    if (!moreCursor) return;
    const requestedFor = restaurantId;
    const part = filter === 'done' ? { endedCursor: moreCursor } : { openCursor: moreCursor };
    const sequence = loadSequence.current;
    const isCurrentLoad = () => stillShown(requestedFor) && sequence === loadSequence.current;
    setLoadingOlder(true);
    try {
      const page = await visitWishApi.listPage(requestedFor, part);
      if (!isCurrentLoad()) return;
      setWishes((current) => [
        ...current,
        ...page.wishes.filter((wish) => !current.some((known) => known.id === wish.id)),
      ]);
      if (part.endedCursor) setEndedCursor(page.endedCursor ?? null);
      else setOpenCursor(page.openCursor ?? null);
    } catch (error) {
      if (!isCurrentLoad()) return;
      toast(apiMessage(error, 'Loading failed'), Severity.ERROR, 'reservations');
    } finally {
      if (isCurrentLoad()) setLoadingOlder(false);
    }
  };

  useEffect(() => {
    if (!linkedWish || loading || loadFailed || loadedFor !== viewScope || appliedLink.current === linkScope) return;
    let active = true;
    setLinkFailed(false);
    const openWish = (wish: VisitWish) => {
      if (!active) return;
      appliedLink.current = linkScope;
      setWishes(current => current.some(entry => entry.id === wish.id) ? current : [...current, wish]);
      setTab('wishes');
      setSearch('');
      setFilter(filterOf(wish));
      setSelectedId(wish.id);
    };
    const wish = wishes.find(entry => entry.id === linkedWish);
    if (wish) openWish(wish);
    else visitWishApi.get(restaurantId, linkedWish).then(openWish).catch(error => {
      if (!active) return;
      if (error?.response?.status === 404) appliedLink.current = linkScope;
      else setLinkFailed(true);
    });
    return () => { active = false; };
  }, [restaurantId, linkedWish, linkScope, viewScope, loading, loadFailed, loadedFor, wishes, linkRetry]);

  const found = useMemo(() => wishes.filter((wish) => matchesSearch(wish, search)), [wishes, search]);
  // The counts follow the search, so matches behind another filter stay visible.
  const counts = useMemo(
    () =>
      found.reduce(
        (sum, wish) => ({ ...sum, [filterOf(wish)]: sum[filterOf(wish)] + 1 }),
        { open: 0, offer: 0, done: 0 } as Record<WishFilter, number>
      ),
    [found]
  );
  const shown = useMemo(() => {
    const list = found.filter((wish) => filterOf(wish) === filter);
    return filter === 'done' ? list : list.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom) || a.createdAt - b.createdAt);
  }, [found, filter]);
  // A searched day (e.g. from the calendar) decides by its own rule; otherwise each wish by the days it covers.
  const day = searchedDate(search);
  const reach = useMemo(() => {
    const byWish = new Map<string, PriorityReach>();
    if (settings) wishes.forEach((wish) => byWish.set(wish.id, priorityReach(wish, settings, day)));
    return byWish;
  }, [wishes, settings, day]);
  const priorityOf = (wish: VisitWish) => reach.get(wish.id) ?? 'none';
  const dayRule = day && settings ? effectiveRuleFor(settings, day) : null;
  const selected = shown.find((wish) => wish.id === selectedId) ?? null;

  useEffect(() => {
    if (selected) detailsRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selected?.id]);

  const replace = (updated: VisitWish) =>
    setWishes((current) => current.map((wish) => (wish.id === updated.id ? { ...wish, ...updated } : wish)));

  const wishActions = useWishActions({ restaurantId, settings, priorityOf, onUpdated: replace, reload: load });

  const headers: ListConfigHeader<VisitWish>[] = [
    {
      field: 'period',
      headerName: 'Wished date',
      headerTranslation: 'reservations',
      displayFunction: (wish) => (
        <>
          <Typography variant="text-3" weight="medium" block>
            {period(wish)}
          </Typography>
          <Typography variant="text-4" color="subdued" block>
            {timeWindow(wish, t)}
            {wish.minLeadHours ? ` · ${t('at least {{hours}} h notice', { hours: wish.minLeadHours })}` : ''}
          </Typography>
        </>
      ),
    },
    {
      field: 'guests',
      headerName: 'Guests',
      headerTranslation: 'reservations',
      width: 70,
      displayFunction: (wish) => (
        <Typography variant="text-3" weight="medium">
          {wish.guests}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Guest',
      headerTranslation: 'reservations',
      displayFunction: (wish) => (
        <button type="button" className="visit-wish-guest-button" aria-expanded={selectedId === wish.id}
          onClick={(event) => { event.stopPropagation(); setSelectedId(wish.id === selectedId ? null : wish.id); }}>
          {wish.contact.name}{wish.vipTags?.length ? ' · VIP' : ''}
        </button>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      headerTranslation: 'reservations',
      displayFunction: (wish) => (
        <>
          <WishStatus wish={wish} />
          {wish.exclusive || liveOffer(wish) ? <ExclusiveCell wish={wish} /> : null}
        </>
      ),
    },
    ...(shown.some((wish) => priorityOf(wish) !== 'none')
      ? [
          {
            field: 'priority',
            headerName: 'Priority',
            headerTranslation: 'reservations' as const,
            width: 110,
            displayFunction: (wish: VisitWish) =>
              priorityOf(wish) === 'none' ? (
                <Typography variant="text-3" color="subdued">
                  –
                </Typography>
              ) : (
                <>
                  <Typography variant="text-3" block>
                    {wish.priority}
                  </Typography>
                  {priorityOf(wish) === 'some' && (
                    <Typography variant="text-4" color="subdued" translation="reservations" block>
                      Only on some days
                    </Typography>
                  )}
                </>
              ),
          },
        ]
      : []),
    {
      field: 'createdAt',
      headerName: 'Received',
      headerTranslation: 'reservations',
      width: 100,
      displayFunction: (wish) => (
        <Typography variant="text-4" color="subdued">
          {toEuropeanDate(dateHelper(wish.createdAt))}
        </Typography>
      ),
    },
  ];

  const renderDetails = (wish: VisitWish) => (
    <WishDetails
      wish={wish}
      restaurantId={restaurantId}
      priority={priorityOf(wish)}
      selectedDay={day}
      actions={wishActions.wishActions}
      priorityActions={wishActions.priorityActions}
      busy={wishActions.busyId === wish.id}
      onClose={() => setSelectedId(null)}
      presentation={small ? 'modal' : 'panel'}
    />
  );

  const currentFilter = FILTERS.find((entry) => entry.id === filter)!;
  const emptySentence = loading
    ? t('Loading…')
    : search.trim()
    ? t('No visit wish matches “{{query}}”.', { query: search.trim() })
    : t(currentFilter.empty);

  return (
    <Box background id="visit-wish-screen">
      <NavBar
        title={t('Visit wishes')}
        className="fixed-nav visit-wish-navbar"
        mainProduct={{
          label: 'Reservations',
          link: (rId) => `/${rId}/reservations/${dateHelper()}/tablePlan`,
        }}
        reservationBook
        right={
          // On a phone the header has no room for a text button next to the global controls.
          small ? (
            <IconButton size="small" aria-label={t('Settings')} title={t('Settings')} linkTo={settingsLink}>
              <SettingsIcon color="inherit" />
            </IconButton>
          ) : (
            <Button variant="outlined" translation="reservations" linkTo={settingsLink}>
              Settings
            </Button>
          )
        }
      />
      <FeatureNavigation date={dateHelper()} paths={paths} current="visitWishes" className="featureNavigation" />
      <Container variant="reservations">
        <Tabs
          id="visit-wish-tabs"
          value={tab}
          onChange={(id) => setTab(id as 'wishes' | 'releaseAlerts')}
          tabs={[
            { id: 'wishes', label: 'Visit wishes', translation: 'reservations' },
            { id: 'releaseAlerts', label: 'Release notifications', translation: 'reservations' },
          ]}
        />
        {tab === 'releaseAlerts' ? (
          <ReleaseAlertsSection restaurantId={restaurantId} />
        ) : loadFailed && !wishes.length ? (
          <Box className="visit-wish-state" role="alert">
            <Typography variant="text-2" translation="reservations" block>
              Visit wishes could not be loaded.
            </Typography>
            <Button variant="outlined" translation="reservations" onClick={load}>
              Try again
            </Button>
          </Box>
        ) : (
          <>
            {linkFailed && (
              <Box className="visit-wish-state" role="alert">
                <Typography variant="text-2" translation="reservations" block>
                  The linked visit wish could not be loaded.
                </Typography>
                <Button variant="outlined" translation="reservations" onClick={() => setLinkRetry(value => value + 1)}>
                  Try again
                </Button>
              </Box>
            )}
            <Box className="visit-wish-toolbar">
              <div className="visit-wish-filters" role="group" aria-label={t('Show')}>
                {FILTERS.map((entry) => (
                  <Button
                    key={entry.id}
                    variant={filter === entry.id ? 'primary' : 'outlined'}
                    aria-pressed={filter === entry.id}
                    onClick={() => {
                      setFilter(entry.id);
                      setSelectedId(null);
                    }}
                  >
                    {/* "+": more of this kind are not loaded yet. */}
                    {`${t(entry.label)} (${counts[entry.id]}${(entry.id === 'done' ? endedCursor : openCursor) ? '+' : ''})`}
                  </Button>
                ))}
              </div>
              <TextField
                id="visit-wish-search"
                type="search"
                autoComplete="off"
                className="visit-wish-search"
                label="Search guest or date"
                labelTranslation="reservations"
                placeholder={t('Name, email or 30.10.')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Box>
            {day && dayRule && (
              <Typography variant="text-3" color="subdued" block className="visit-wish-day-note">
                {`${t('On {{date}}: {{order}}', { date: europeanDate(day), order: t(orderLabel(dayRule.usePriority)) })} · ${t(
                  dayRule.source === 'date' ? 'Exception for this day' : 'Restaurant standard'
                )}`}
              </Typography>
            )}
            {small ? (
              <>
                <WishCards
                  wishes={loading ? [] : shown}
                  emptySentence={emptySentence}
                  selectedId={selected?.id ?? null}
                  onOpen={setSelectedId}
                  actions={wishActions.actions}
                  busyId={wishActions.busyId}
                  priorityOf={priorityOf}
                />
                {selected && renderDetails(selected)}
              </>
            ) : (
              <div className={`visit-wish-workspace ${selected ? 'with-details' : ''}`}>
                <div className="visit-wish-table">
                  <GTable
                    headers={headers}
                    data={loading ? [] : shown}
                    contextActions={wishActions.actions}
                    emptySentence={emptySentence}
                    onRowClick={(id) => setSelectedId(id === selectedId ? null : id)}
                    styleFunction={(wish) =>
                      wish.id === selectedId
                        ? { background: 'var(--color-bg)', outline: '1px solid var(--color-primary)', outlineOffset: -1 }
                        : undefined
                    }
                  />
                </div>
                {selected && (
                  <div ref={detailsRef} className="visit-wish-workspace-details">
                    {renderDetails(selected)}
                  </div>
                )}
              </div>
            )}
            {moreCursor && !loading && (
              <Button variant="outlined" translation="reservations" onClick={loadMore} loading={loadingOlder}>
                {filter === 'done' ? 'Load older requests' : 'Load more requests'}
              </Button>
            )}
          </>
        )}
        {wishActions.modal}
      </Container>
    </Box>
  );
};

export default VisitWishList;
